// Auth against the authd REST endpoints (proxied under /auth) plus token
// management for GraphQL requests. Access token is mirrored to localStorage so
// reloads stay logged in; refresh happens proactively on a timer and lazily on
// 401, deduplicated by a single in-flight promise.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

type TokenResponse = {
  person_id: string;
  access_token: string;
  refresh_token: string;
  expires_in: number;
};

const STORAGE_KEY = "gaea.auth";

type Stored = { personId: string; accessToken: string; refreshToken: string; expiresAt: number };

let stored: Stored | null = loadStored();
let refreshInFlight: Promise<boolean> | null = null;
const listeners = new Set<() => void>();

function loadStored(): Stored | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Stored) : null;
  } catch {
    return null;
  }
}

function setStored(next: Stored | null) {
  stored = next;
  if (next) localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  else localStorage.removeItem(STORAGE_KEY);
  listeners.forEach((fn) => fn());
}

function applyTokens(t: TokenResponse) {
  setStored({
    personId: t.person_id,
    accessToken: t.access_token,
    refreshToken: t.refresh_token,
    expiresAt: Date.now() + t.expires_in * 1000,
  });
}

export class AuthError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
  }
}

async function authRequest(path: string, body: unknown): Promise<TokenResponse> {
  const res = await fetch(`/auth${path}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) throwAuthError(json, res.status);
  return json as unknown as TokenResponse;
}

function throwAuthError(json: Record<string, unknown>, status: number): never {
  throw new AuthError(typeof json.error === "string" ? json.error : `auth failed (${status})`, status);
}

/** Request a password-reset email. Always resolves on 204 regardless of whether the address exists. */
export async function forgotPassword(email: string): Promise<void> {
  const res = await fetch("/auth/forgot", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throwAuthError((await res.json().catch(() => ({}))) as Record<string, unknown>, res.status);
}

/** Redeem a reset token for a new password; logs the user in with the returned tokens. */
export async function resetPassword(token: string, password: string): Promise<void> {
  applyTokens(await authRequest("/reset", { token, password }));
}

/** Change the logged-in user's password (requires the current one). */
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  const res = await authFetch("/auth/change-password", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  if (!res.ok) throwAuthError((await res.json().catch(() => ({}))) as Record<string, unknown>, res.status);
}

async function refresh(): Promise<boolean> {
  if (!stored) return false;
  refreshInFlight ??= authRequest("/refresh", { refresh_token: stored.refreshToken })
    .then((t) => {
      applyTokens(t);
      return true;
    })
    .catch(() => {
      setStored(null);
      return false;
    })
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

/** fetch that injects the access token and retries once through a refresh on 401. */
export async function authFetch(input: string, init?: RequestInit): Promise<Response> {
  const doFetch = () => {
    const headers = new Headers(init?.headers);
    if (stored) headers.set("authorization", `Bearer ${stored.accessToken}`);
    return fetch(input, { ...init, headers });
  };
  if (stored && Date.now() >= stored.expiresAt - 30_000) await refresh();
  let res = await doFetch();
  if (res.status === 401 && stored && (await refresh())) res = await doFetch();
  return res;
}

export function currentPersonId(): string | null {
  return stored?.personId ?? null;
}

/**
 * Postgres role claim from the JWT payload. Admins are marked by role
 * "gaea_admin" — deliberately not exposed through GraphQL, so the client
 * decodes it here. Display gating only; the server enforces everything via RLS.
 */
function tokenRole(token: string | null | undefined): string | null {
  const payload = token?.split(".")[1];
  if (!payload) return null;
  try {
    const claims = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/"))) as { role?: unknown };
    return typeof claims.role === "string" ? claims.role : null;
  } catch {
    return null;
  }
}

function currentIsAdmin(): boolean {
  return tokenRole(stored?.accessToken) === "gaea_admin";
}

/** Account state from authd — the authoritative source for email and verification. */
export type Me = {
  person_id: string;
  email: string;
  display_name: string;
  email_verified: boolean;
  created_at: string;
};

/**
 * The logged-in account from GET /auth/me. Disabled (data undefined) when
 * anonymous. Key includes personId so a login switch never serves the previous
 * account from cache.
 */
export function useMe(): UseQueryResult<Me> {
  const { personId } = useAuth();
  return useQuery({
    queryKey: ["me", personId],
    enabled: personId != null,
    queryFn: async () => {
      const res = await authFetch("/auth/me");
      if (!res.ok) throwAuthError((await res.json().catch(() => ({}))) as Record<string, unknown>, res.status);
      return (await res.json()) as Me;
    },
  });
}

/** Ask authd to email a verification link (dev: the link lands in the auth service logs). */
export async function sendVerificationEmail(): Promise<void> {
  const res = await authFetch("/auth/verify/send", { method: "POST" });
  if (!res.ok) throwAuthError((await res.json().catch(() => ({}))) as Record<string, unknown>, res.status);
}

/**
 * Redeem an email-verification token. 401 means invalid/expired. Deliberately
 * plain fetch: authFetch would misread that 401 as a stale access token and
 * burn a refresh round-trip, and the endpoint doesn't need a bearer.
 */
export async function confirmEmail(token: string): Promise<void> {
  const res = await fetch("/auth/verify/confirm", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) throwAuthError((await res.json().catch(() => ({}))) as Record<string, unknown>, res.status);
}

type AuthContextValue = {
  personId: string | null;
  /** True when the access token's role claim is gaea_admin (UI gating only — RLS is the enforcement). */
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [personId, setPersonId] = useState(stored?.personId ?? null);
  const [isAdmin, setIsAdmin] = useState(currentIsAdmin);

  useEffect(() => {
    const onChange = () => {
      setPersonId(stored?.personId ?? null);
      setIsAdmin(currentIsAdmin());
    };
    listeners.add(onChange);
    return () => void listeners.delete(onChange);
  }, []);

  // Proactive refresh so the access token never lapses mid-session.
  useEffect(() => {
    if (!personId) return;
    const delay = Math.max((stored?.expiresAt ?? 0) - Date.now() - 60_000, 10_000);
    const timer = setTimeout(() => void refresh(), delay);
    return () => clearTimeout(timer);
  }, [personId]);

  const login = useCallback(async (email: string, password: string) => {
    applyTokens(await authRequest("/login", { email, password }));
  }, []);
  const register = useCallback(async (email: string, password: string, displayName: string) => {
    applyTokens(await authRequest("/register", { email, password, display_name: displayName }));
  }, []);
  const logout = useCallback(() => setStored(null), []);

  const value = useMemo(
    () => ({ personId, isAdmin, login, register, logout }),
    [personId, isAdmin, login, register, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}
